import React, { useEffect, useState, useRef } from 'react'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { Typography } from '@material-ui/core'
import EditableDialogWrapper from './editableDialogWrapper'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { ServiceFeatureFlagNamesEnums } from '../../constants/serviceFeatureFlagName.enums'
import { featureFlagsActions } from '../../actions/featureFlags.actions'
import { enqueueSnackbar } from '../../actions/notification.actions'
import { editorTools } from '../../constants/editor.constants'
import { applyToolbarAriaLabels, focusEditorWhenReady } from '../../helpers/editorHelper'
import { IsFeatureFlagEnabled } from '../../providers/globalFeatureFlag.provider'
import { GlobalFeatureFlagConstants } from '../../constants/globalFeatureFlag.constants'

let nextHtmlDialogId = 0

const HtmlDialog = ({
  title,
  description,
  open,
  currentHtml,
  onConfirm,
  onReset,
  onClose,
  fontSize,
  fontFamily,
  onChange,
  isMandatory = true,
  resetButtonVisible = true,
  confirmButtonText,
}) => {
  const isViewHtmlEditorFixEnabled = IsFeatureFlagEnabled(
    GlobalFeatureFlagConstants.FixDocServiceViewHtmlEditor_81045
  )
  const [hasEntered, setHasEntered] = useState(false)
  const [dialogId] = useState(() => `html-editor-dialog-${++nextHtmlDialogId}`)
  const dispatch = useDispatch()

  const [html, setHtml] = useState(currentHtml)
  const contentEditorWrapperRef = useRef()
  const contentEditorRef = useRef()

  const getEditorInstance = () => {
    const $ = window.jQuery
    return $(contentEditorRef.current).data('kendoEditor')
  }

  const [
    isConfirmCancelNonMandatoryFieldsEnabled,
    setIsConfirmCancelNonMandatoryFieldsEnabled,
  ] = useState(false)
  const { items: featureFlags } = useSelector(state => state.featureFlags)
  
  useEffect(() => {
    dispatch(featureFlagsActions.get())
  }, [dispatch])

  useEffect(() => {
    async function loadConfirmCancelFlag() {
      try {
        const confirmCancelNonMandatoryFields = featureFlags.find(
          f =>
            f.featureName ===
            ServiceFeatureFlagNamesEnums.Documents_ConfirmCancelNonMandatoryFields
        )
        if (confirmCancelNonMandatoryFields) {
          setIsConfirmCancelNonMandatoryFieldsEnabled(
            confirmCancelNonMandatoryFields.isEnabled
          )
        }
      } catch (exception) {
        dispatch(
          enqueueSnackbar({
            message: 'Failed to load Feature Flags',
            options: { variant: 'error' },
          })
        )
      }
    }
  
    if (featureFlags?.length) {
      loadConfirmCancelFlag()
    }
  }, [featureFlags, dispatch])

  useEffect(() => {
    if (open) {
      setHtml(currentHtml)
    }
  }, [currentHtml, open])

  const onContentEditorChange = () => contentEditorChanged()

  const onEditorSelect = () => {
    contentEditorChanged()
    const editor = getEditorInstance()
    if (editor) {
      applyToolbarAriaLabels(editor)
    }
  }

  const contentEditorChanged = () => {
    const editor = getEditorInstance()
    if (editor) {
      const newValue = editor.value()
      setHtml(newValue)

      if (onChange) {
        onChange(newValue) // Notify the parent component of the change
      }
    }
  }

  useEffect(() => {
    if (hasEntered) {
      const $ = window.jQuery

      if (contentEditorRef.current) {
        $(contentEditorRef.current).kendoEditor({
          stylesheets: [
            `/css/editor/font-family-${fontFamily}.css`,
            `/css/editor/font-size-${fontSize}.css`,
          ],
          tools: editorTools,
          ...(isViewHtmlEditorFixEnabled
            ? {
                dialogOptions: {
                  // Use a selector: Kendo recursively copies initial options.
                  // Keep popups inside the Material UI dialog's focus boundary.
                  appendTo: `#${dialogId} .MuiDialog-container`,
                },
              }
            : {}),
          change: onContentEditorChange,
          keyup: onContentEditorChange,
          input: onContentEditorChange,
          select: onEditorSelect,
          execute: onEditorSelect,
          paste: onContentEditorChange,
        })

        const editor = getEditorInstance()
        if (editor) {
          editor.value(html)
          focusEditorWhenReady(editor)
          applyToolbarAriaLabels(editor)
        }

        contentEditorWrapperRef.current.style.display = 'block'

        return () => {
          const editorInstance = getEditorInstance()
          if (editorInstance) {
            editorInstance.destroy()
          }
        }
      } else {
        console.error('contentEditorRef is not defined.')
      }
    }
  }, [hasEntered, dialogId, isViewHtmlEditorFixEnabled])

  return (
    <EditableDialogWrapper
      id={isViewHtmlEditorFixEnabled ? dialogId : undefined}
      title={title}
      open={open}
      onClose={onClose}
      onEntered={() => setHasEntered(true)}>
      <DialogContent dividers>
        {description && <Typography gutterBottom>{description}</Typography>}
        <div style={{ height: 400 }}>
          <div ref={contentEditorWrapperRef} style={{ display: 'none' }}>
            <textarea style={{ height: 380 }} ref={contentEditorRef} />
          </div>
        </div>
      </DialogContent>
      {isConfirmCancelNonMandatoryFieldsEnabled ? (
        <DialogActions>
          {resetButtonVisible && (
            <Button
              onClick={isMandatory ? onReset : () => onConfirm('')}
              color='secondary'>
              {isMandatory ? 'Reset' : 'Remove from letter'}
            </Button>
          )}
          <Button
            variant='contained'
            onClick={() => onConfirm(html)}
            color='secondary'>
            {confirmButtonText ||
              (isMandatory ? 'Confirm' : 'Confirm inclusion in letter')}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions>
          <Button onClick={onReset} color='secondary'>
            Reset
          </Button>
          <Button
            variant='contained'
            onClick={() => onConfirm(html)}
            color='secondary'>
            Confirm
          </Button>
        </DialogActions>
      )}
    </EditableDialogWrapper>
  )
}
HtmlDialog.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  open: PropTypes.bool.isRequired,
  currentHtml: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  fontSize: PropTypes.string,
  fontFamily: PropTypes.string,
  onChange: PropTypes.func,
  isMandatory: PropTypes.bool,
  resetButtonVisible: PropTypes.bool,
  confirmButtonText: PropTypes.string,
}

export default HtmlDialog
